/**
 * Карточка работы: тип, длительность (задана или от объёма и выработки), календарь, ограничения,
 * факт, коды и связи с предшественниками и последователями. Пока карточка открыта, работа
 * заблокирована для других (ПЛ-31).
 */
import {
  ACTIVITY_TYPE_TITLES,
  ACTIVITY_TYPES,
  type ActivityInput,
  type ActivityRow,
  CONSTRAINT_TITLES,
  CONSTRAINT_TYPES,
  durationFromQuantity,
  LAG_CALENDAR_TITLES,
  LAG_CALENDARS,
  LEVEL_TITLES,
  LEVELS,
  RELATION_SHORT,
  RELATION_TYPES,
  type RelationRow,
  type ScheduleOp,
} from '@epc/contracts';
import { Lock, Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { HistoryButton } from '../../components/History';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge } from '../../ui/feedback';
import { Input, Select, Textarea } from '../../ui/form';
import { RelatedArticles } from '../kb/RelatedArticles';
import { ActivityHolds } from './ActivityHolds';
import { ActivityImpact, ActivityPurchases } from './ActivityImpact';
import { ActivityResources } from './ActivityResources';
import { ActivityScope } from './ActivityScope';
import { ActivityStepsBlock } from './ActivitySteps';
import { type ExternalActivity, ExternalPicker } from './ExternalPicker';
import { LField } from './LField';
import { barDates, fmtDur, fmtMoment, hpd, type View } from './model';

type Rel = RelationRow & { _new?: boolean; _del?: boolean };

const num = (s: string): number | null => {
  const v = Number(s.replace(/\s/g, '').replace(',', '.'));
  return s.trim() === '' || !Number.isFinite(v) ? null : v;
};
const day = (m: string | null | undefined) => (m ? m.slice(0, 10) : '');
const time = (m: string | null | undefined) => (m && m.length >= 16 ? m.slice(11, 16) : '');
const join = (d: string, t: string, fallback: string) => (d ? `${d}T${t || fallback}` : null);

export function ActivityDialog({
  view,
  activityId,
  newIn,
  onClose,
  onCommit,
}: {
  view: View;
  /** Работа для правки; null — новая */
  activityId: string | null;
  /** Для новой работы: узел ИСР и предшественник */
  newIn?: { wbsId: string | null; afterId?: string | null };
  onClose: () => void;
  onCommit: (ops: ScheduleOp[], label: string) => void;
}) {
  const a = activityId ? view.activities.find((x) => x.id === activityId) : undefined;
  const isNew = !a;
  // База знаний — только сотрудникам компании: внешним панель статей не показывается (БЗ-08)
  const staff = Boolean(useMe().tenant?.staff);
  const [id] = useState(() => a?.id ?? crypto.randomUUID());
  const canEdit = view.can.edit;
  const approved = view.schedule.status === 'approved';
  const [busy, setBusy] = useState<string | null>(null);

  // Блокировка на время правки: продлевается каждые 2 минуты, снимается при закрытии
  useEffect(() => {
    if (!a || !canEdit) return;
    let alive = true;
    const lock = () =>
      api<{ locked: string[]; busy: Array<{ name: string }> }>(`/schedules/${view.schedule.id}/locks`, {
        body: { activityIds: [a.id] },
      })
        .then((r) => alive && setBusy(r.busy[0]?.name ?? null))
        .catch(() => undefined);
    lock();
    const t = window.setInterval(lock, 120_000);
    return () => {
      alive = false;
      window.clearInterval(t);
      api(`/schedules/${view.schedule.id}/locks`, { body: { activityIds: [a.id], release: true } }).catch(
        () => undefined,
      );
    };
  }, [a?.id, canEdit, view.schedule.id, a]);

  const readOnly = !canEdit || Boolean(busy);
  const h = hpd(view, a?.calendarId);
  const [f, setF] = useState(() => ({
    code: a?.code ?? '',
    name: a?.name ?? '',
    type: a?.type ?? 'task',
    wbsId: a?.wbsId ?? newIn?.wbsId ?? '',
    level: a?.level ?? 'detailed',
    calendarId: a?.calendarId ?? '',
    durationMode: a?.durationMode ?? 'fixed',
    days: a ? String(Math.round((a.durationMin / (h * 60)) * 100) / 100).replace('.', ',') : '5',
    quantity: a?.quantity != null ? String(a.quantity) : '',
    unit: a?.unit ?? '',
    ratePerDay: a?.ratePerDay != null ? String(a.ratePerDay) : '',
    crews: a?.crews != null ? String(a.crews) : '1',
    constraintType: a?.constraintType ?? 'asap',
    constraintDay: day(a?.constraintDate),
    constraintTime: time(a?.constraintDate),
    deadline: day(a?.deadline),
    actualStartDay: day(a?.actualStart),
    actualStartTime: time(a?.actualStart),
    actualFinishDay: day(a?.actualFinish),
    actualFinishTime: time(a?.actualFinish),
    percent: String(a?.percent ?? 0),
    remainingDays:
      a?.remainingMin != null
        ? String(Math.round((a.remainingMin / (h * 60)) * 100) / 100).replace('.', ',')
        : '',
    responsibleId: a?.responsibleId ?? '',
    contractorId: a?.contractorId ?? '',
    zoneId: a?.zoneId ?? '',
    workType: a?.workType ?? '',
    note: a?.note ?? '',
    priority: a?.priority != null ? String(a.priority) : '',
    optDays:
      a?.durationOptMin != null
        ? String(Math.round((a.durationOptMin / (h * 60)) * 100) / 100).replace('.', ',')
        : '',
    pessDays:
      a?.durationPessMin != null
        ? String(Math.round((a.durationPessMin / (h * 60)) * 100) / 100).replace('.', ',')
        : '',
    locFrom: a?.locationFrom != null ? String(a.locationFrom) : '',
    locTo: a?.locationTo != null ? String(a.locationTo) : '',
    codes: { ...(a?.codes ?? {}) } as Record<string, string>,
  }));
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  /** Процент считается по шагам выполнения (ПЛ-36): ручной ввод выключен */
  const [bySteps, setBySteps] = useState(false);
  const [rels, setRels] = useState<Rel[]>(() => {
    const own = view.relations.filter((r) => r.succId === id || r.predId === id);
    if (isNew && newIn?.afterId)
      own.push({
        id: crypto.randomUUID(),
        predId: newIn.afterId,
        succId: id,
        type: 'FS',
        lagMin: 0,
        lagUnit: 'd',
        lagPct: null,
        lagCalendar: null,
        slack: null,
        driving: false,
      });
    return own.map((r) => ({ ...r, _new: !view.relations.includes(r) }));
  });
  const [addPred, setAddPred] = useState('');
  const [addSucc, setAddSucc] = useState('');
  const actById = useMemo(() => new Map(view.activities.map((x) => [x.id, x])), [view.activities]);
  const ext = useMemo(() => new Map(view.external.map((x) => [x.id, x])), [view.external]);
  const [picked, setPicked] = useState<Map<string, ExternalActivity>>(() => new Map());
  const nameOf = (rid: string) => {
    const x = actById.get(rid);
    if (x) return `${x.code} ${x.name}`;
    const e = ext.get(rid) ?? picked.get(rid);
    return e ? `${e.projectCode} · ${e.code} ${e.name}` : 'работа другого проекта';
  };
  const ms = f.type === 'start_ms' || f.type === 'finish_ms';
  const calH = f.calendarId ? hpd(view, f.calendarId) : hpd(view, null);
  const fromQty =
    f.durationMode === 'quantity'
      ? durationFromQuantity(num(f.quantity) ?? 0, num(f.ratePerDay) ?? 0, num(f.crews) ?? 1, calH)
      : null;
  const [err, setErr] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!f.name.trim()) return setErr('Укажите название работы');
    const days = num(f.days);
    if (!ms && f.durationMode === 'fixed' && (days === null || days < 0))
      return setErr('Укажите длительность в днях');
    if (f.constraintType !== 'asap' && f.constraintType !== 'alap' && !f.constraintDay)
      return setErr('Для ограничения нужна дата');
    const data: ActivityInput = {
      name: f.name.trim(),
      type: f.type,
      wbsId: f.wbsId || null,
      level: f.level,
      calendarId: f.calendarId || null,
      durationMode: f.durationMode,
      constraintType: f.constraintType,
      constraintDate:
        f.constraintType === 'asap' || f.constraintType === 'alap'
          ? null
          : join(
              f.constraintDay,
              f.constraintTime,
              f.constraintType.startsWith('f') || f.constraintType === 'mfo' ? '18:00' : '00:00',
            ),
      deadline: f.deadline ? `${f.deadline}T23:59` : null,
      actualStart: join(f.actualStartDay, f.actualStartTime, '00:00'),
      actualFinish: join(f.actualFinishDay, f.actualFinishTime, '23:59'),
      percent: Math.min(100, Math.max(0, num(f.percent) ?? 0)),
      remainingMin:
        f.actualStartDay && !f.actualFinishDay && f.remainingDays
          ? Math.round((num(f.remainingDays) ?? 0) * calH * 60)
          : null,
      responsibleId: f.responsibleId || null,
      contractorId: f.contractorId || null,
      zoneId: f.zoneId || null,
      workType: f.workType.trim() || null,
      note: f.note.trim() || null,
      priority: f.priority ? Number(f.priority) : null,
      durationOptMin: f.optDays.trim() ? Math.round((num(f.optDays) ?? 0) * calH * 60) : null,
      durationPessMin: f.pessDays.trim() ? Math.round((num(f.pessDays) ?? 0) * calH * 60) : null,
      locationFrom: f.locFrom.trim() ? num(f.locFrom) : null,
      locationTo: f.locTo.trim() ? num(f.locTo) : null,
    };
    if (
      data.durationOptMin != null &&
      data.durationPessMin != null &&
      data.durationOptMin > data.durationPessMin
    )
      return setErr('Оптимистичная длительность больше пессимистичной');
    if (f.code.trim()) data.code = f.code.trim();
    if (f.durationMode === 'quantity') {
      data.quantity = num(f.quantity);
      data.unit = f.unit.trim() || null;
      data.ratePerDay = num(f.ratePerDay);
      data.crews = Math.max(1, Math.round(num(f.crews) ?? 1));
      if (fromQty) data.durationMin = fromQty;
    } else if (!ms) data.durationMin = Math.round((days ?? 0) * calH * 60);
    // Только изменённые поля — чтобы отмена возвращала ровно их
    let changed = data;
    if (a) {
      changed = {};
      for (const [k, v] of Object.entries(data)) {
        const cur = (a as unknown as Record<string, unknown>)[k] ?? null;
        if ((v ?? null) !== cur) (changed as Record<string, unknown>)[k] = v;
      }
    }
    const ops: ScheduleOp[] = [];
    if (isNew) ops.push({ op: 'activity', id, create: true, data });
    else if (Object.keys(changed).length) ops.push({ op: 'activity', id, data: changed });
    for (const [typeId, valueId] of Object.entries(f.codes))
      if ((a?.codes[typeId] ?? '') !== valueId)
        ops.push({ op: 'code', activityId: id, typeId, valueId: valueId || null });
    for (const r of rels) {
      const orig = view.relations.find((x) => x.id === r.id);
      if (r._del) {
        if (orig) ops.push({ op: 'relation.delete', id: r.id });
        continue;
      }
      if (
        !orig ||
        orig.type !== r.type ||
        orig.lagMin !== r.lagMin ||
        orig.lagUnit !== r.lagUnit ||
        orig.lagPct !== r.lagPct ||
        orig.lagCalendar !== r.lagCalendar
      )
        ops.push({
          op: 'relation',
          id: r.id,
          create: !orig,
          predId: r.predId,
          succId: r.succId,
          type: r.type,
          lagMin: r.lagMin,
          lagUnit: r.lagUnit,
          lagPct: r.lagUnit === 'pct' ? r.lagPct : null,
          lagCalendar: r.lagCalendar,
        });
    }
    if (ops.length) onCommit(ops, isNew ? `Новая работа «${data.name}»` : `Правка ${a!.code}`);
    onClose();
  };

  const remove = () => {
    if (!a) return;
    if (
      !window.confirm(
        `Удалить работу ${a.code} «${a.name}» вместе с её связями? Отменить можно кнопкой «Отменить».`,
      )
    )
      return;
    onCommit([{ op: 'activity.delete', id: a.id }], `Удаление ${a.code}`);
    onClose();
  };

  const relRow = (r: Rel, side: 'pred' | 'succ') => {
    const other = side === 'pred' ? r.predId : r.succId;
    const unitH = r.lagUnit === 'h';
    const lagVal = r.lagUnit === 'pct' ? (r.lagPct ?? 0) : unitH ? r.lagMin / 60 : r.lagMin / (calH * 60);
    const upd = (p: Partial<Rel>) => setRels((list) => list.map((x) => (x.id === r.id ? { ...x, ...p } : x)));
    return (
      <li
        key={r.id}
        className={`grid grid-cols-[1fr_auto] gap-2 border-t border-line py-2 first:border-t-0 ${r._del ? 'opacity-40' : ''}`}
      >
        <div className="min-w-0">
          <div className="truncate text-[14px]" title={nameOf(other)}>
            {nameOf(other)}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Select
              className="h-11 w-[84px] md:h-9"
              value={r.type}
              disabled={readOnly || approved}
              onChange={(e) => upd({ type: e.target.value as Rel['type'] })}
              aria-label="Тип связи"
            >
              {RELATION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {RELATION_SHORT[t]}
                </option>
              ))}
            </Select>
            <Input
              className="h-11 w-[72px] md:h-9"
              inputMode="decimal"
              aria-label="Задержка"
              disabled={readOnly || approved}
              defaultValue={String(Math.round(lagVal * 100) / 100).replace('.', ',')}
              onBlur={(e) => {
                const v = num(e.target.value) ?? 0;
                if (r.lagUnit === 'pct') upd({ lagPct: v, lagMin: 0 });
                else upd({ lagMin: Math.round(unitH ? v * 60 : v * calH * 60) });
              }}
            />
            <Select
              className="h-11 w-[78px] md:h-9"
              value={r.lagUnit}
              disabled={readOnly || approved}
              aria-label="Единица задержки"
              onChange={(e) =>
                upd({
                  lagUnit: e.target.value as Rel['lagUnit'],
                  lagMin: 0,
                  lagPct: e.target.value === 'pct' ? 0 : null,
                })
              }
            >
              <option value="d">дн</option>
              <option value="h">ч</option>
              <option value="pct">%</option>
            </Select>
            <Select
              className="h-11 w-[150px] md:h-9"
              value={r.lagCalendar ?? ''}
              disabled={readOnly || approved}
              aria-label="Календарь задержки"
              onChange={(e) => upd({ lagCalendar: (e.target.value || null) as Rel['lagCalendar'] })}
            >
              <option value="">Календарь по графику</option>
              {LAG_CALENDARS.map((c) => (
                <option key={c} value={c}>
                  {LAG_CALENDAR_TITLES[c]}
                </option>
              ))}
            </Select>
            {r.driving ? (
              <Badge tone="danger">ведущая</Badge>
            ) : r.slack != null && r.slack > 0 ? (
              <span className="text-[12px] text-ink-2">резерв связи {fmtDur(r.slack, calH)}</span>
            ) : null}
          </div>
        </div>
        {!readOnly && !approved ? (
          <IconButton
            label={r._del ? 'Вернуть связь' : 'Удалить связь'}
            onClick={() => upd({ _del: !r._del })}
            className="size-11 md:size-9"
          >
            <Trash2 className="size-4" />
          </IconButton>
        ) : null}
      </li>
    );
  };

  const addRel = (side: 'pred' | 'succ', other: string) => {
    if (!other || other === id) return;
    if (rels.some((r) => !r._del && (side === 'pred' ? r.predId === other : r.succId === other))) return;
    setRels((l) => [
      ...l,
      {
        id: crypto.randomUUID(),
        predId: side === 'pred' ? other : id,
        succId: side === 'pred' ? id : other,
        type: 'FS',
        lagMin: 0,
        lagUnit: 'd',
        lagPct: null,
        lagCalendar: null,
        slack: null,
        driving: false,
        _new: true,
      },
    ]);
  };
  const others = view.activities
    .filter((x) => x.id !== id)
    .sort((x, y) => x.code.localeCompare(y.code, 'ru', { numeric: true }));
  const d = a?.dates;
  const bl = a ? view.baselineDates[a.id] : undefined;
  const { finish } = a ? barDates(a) : { finish: null };

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      width="lg"
      title={isNew ? 'Новая работа' : `${a.code} ${a.name}`}
      description={
        busy ? (
          <span className="inline-flex items-center gap-1 text-warning">
            <Lock className="size-3.5" /> Работу сейчас правит {busy} — карточка только для просмотра
          </span>
        ) : approved ? (
          'График утверждён: сроки и логика меняются через запрос на изменение, факт вносится как обычно'
        ) : undefined
      }
      footer={
        <>
          {a ? <HistoryButton table="activities" rowId={a.id} title={`${a.code} ${a.name}`} /> : null}
          {a && !readOnly && !approved ? (
            <Button variant="danger" onClick={remove} className="mr-auto max-md:h-11">
              Удалить
            </Button>
          ) : (
            <span className="mr-auto" />
          )}
          <Button onClick={onClose} className="max-md:h-11">
            {readOnly ? 'Закрыть' : 'Отмена'}
          </Button>
          {!readOnly ? (
            <Button variant="primary" type="submit" form="activity-form" className="max-md:h-11">
              {isNew ? 'Добавить работу' : 'Сохранить'}
            </Button>
          ) : null}
        </>
      }
    >
      <form id="activity-form" onSubmit={submit} className="flex flex-col gap-5">
        {d ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-surface-2 px-3 py-2.5 text-[13px] sm:grid-cols-4">
            <span className="text-ink-2">Раннее начало</span>
            <span className="num">{fmtMoment(d.es, true)}</span>
            <span className="text-ink-2">Раннее окончание</span>
            <span className="num">{fmtMoment(d.ef, true)}</span>
            <span className="text-ink-2">Позднее начало</span>
            <span className="num">{fmtMoment(d.ls, true)}</span>
            <span className="text-ink-2">Позднее окончание</span>
            <span className="num">{fmtMoment(d.lf, true)}</span>
            <span className="text-ink-2">Полный резерв</span>
            <span className={`num ${d.tf != null && d.tf < 0 ? 'text-danger' : ''}`}>{fmtDur(d.tf, h)}</span>
            <span className="text-ink-2">Свободный резерв</span>
            <span className="num">{fmtDur(d.ff, h)}</span>
            {bl ? (
              <>
                <span className="text-ink-2">Базовый план</span>
                <span className="num">
                  {fmtMoment(bl[0])} — {fmtMoment(bl[1])}
                </span>
                <span className="text-ink-2">Отклонение окончания</span>
                <span className="num">
                  {finish ? (finish > bl[1] ? 'позже' : finish < bl[1] ? 'раньше' : 'в срок') : '—'}
                </span>
              </>
            ) : null}
            <span className="col-span-2 sm:col-span-4">
              {d.critical ? (
                <Badge tone="danger">критическая</Badge>
              ) : d.nearCritical ? (
                <Badge tone="warning">почти критическая</Badge>
              ) : null}
            </span>
          </div>
        ) : null}

        <fieldset disabled={readOnly} className="grid grid-cols-1 gap-4 sm:grid-cols-[140px_1fr]">
          <LField label="Код" hint={isNew ? 'Пусто — присвоится сам' : undefined}>
            <Input
              value={f.code}
              onChange={(e) => set('code', e.target.value)}
              maxLength={40}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Название">
            <Input
              value={f.name}
              onChange={(e) => set('name', e.target.value)}
              maxLength={300}
              required
              className="max-md:h-11"
            />
          </LField>
        </fieldset>
        <fieldset disabled={readOnly || approved} className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <LField label="Тип">
            <Select
              value={f.type}
              onChange={(e) => set('type', e.target.value as ActivityRow['type'])}
              className="max-md:h-11"
            >
              {ACTIVITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {ACTIVITY_TYPE_TITLES[t]}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Узел ИСР">
            <Select value={f.wbsId} onChange={(e) => set('wbsId', e.target.value)} className="max-md:h-11">
              <option value="">Без узла</option>
              {view.wbs.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code ? `${w.code} ` : ''}
                  {w.name}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Уровень графика">
            <Select
              value={f.level}
              onChange={(e) => set('level', e.target.value as ActivityRow['level'])}
              className="max-md:h-11"
            >
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  {LEVEL_TITLES[l]}
                </option>
              ))}
            </Select>
          </LField>
        </fieldset>

        {!ms ? (
          <fieldset disabled={readOnly || approved} className="flex flex-col gap-3">
            <legend className="mb-2 text-[14px] font-semibold">Длительность</legend>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={f.durationMode === 'fixed' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => set('durationMode', 'fixed')}
                className="max-md:h-11"
              >
                Задана
              </Button>
              <Button
                variant={f.durationMode === 'quantity' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => set('durationMode', 'quantity')}
                className="max-md:h-11"
              >
                От объёма и выработки
              </Button>
            </div>
            {f.durationMode === 'fixed' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <LField label="Рабочих дней" hint={`День — ${String(calH).replace('.', ',')} ч`}>
                  <Input
                    inputMode="decimal"
                    value={f.days}
                    onChange={(e) => set('days', e.target.value)}
                    className="max-md:h-11"
                  />
                </LField>
                <CalendarField view={view} value={f.calendarId} onChange={(v) => set('calendarId', v)} />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                <LField label="Объём">
                  <Input
                    inputMode="decimal"
                    value={f.quantity}
                    onChange={(e) => set('quantity', e.target.value)}
                    className="max-md:h-11"
                  />
                </LField>
                <LField label="Ед.">
                  <Input
                    value={f.unit}
                    onChange={(e) => set('unit', e.target.value)}
                    maxLength={20}
                    className="max-md:h-11"
                  />
                </LField>
                <LField label="Выработка звена в день">
                  <Input
                    inputMode="decimal"
                    value={f.ratePerDay}
                    onChange={(e) => set('ratePerDay', e.target.value)}
                    className="max-md:h-11"
                  />
                </LField>
                <LField label="Звеньев">
                  <Input
                    inputMode="numeric"
                    value={f.crews}
                    onChange={(e) => set('crews', e.target.value)}
                    className="max-md:h-11"
                  />
                </LField>
                <LField label="Получится">
                  <div className="num flex h-10 items-center text-[14px] max-md:h-11">
                    {fromQty ? fmtDur(fromQty, calH) : '—'}
                  </div>
                </LField>
                <div className="col-span-2 sm:col-span-5">
                  <CalendarField view={view} value={f.calendarId} onChange={(v) => set('calendarId', v)} />
                </div>
              </div>
            )}
          </fieldset>
        ) : null}

        <fieldset disabled={readOnly || approved} className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <legend className="mb-2 text-[14px] font-semibold sm:col-span-3">Ограничение дат</legend>
          <LField label="Ограничение">
            <Select
              value={f.constraintType}
              onChange={(e) => set('constraintType', e.target.value as ActivityRow['constraintType'])}
              className="max-md:h-11"
            >
              {CONSTRAINT_TYPES.map((c) => (
                <option key={c} value={c}>
                  {CONSTRAINT_TITLES[c]}
                </option>
              ))}
            </Select>
          </LField>
          {f.constraintType !== 'asap' && f.constraintType !== 'alap' ? (
            <LField label="Дата ограничения">
              <Input
                type="date"
                value={f.constraintDay}
                onChange={(e) => set('constraintDay', e.target.value)}
                className="max-md:h-11"
              />
            </LField>
          ) : (
            <span className="hidden sm:block" />
          )}
          <LField label="Крайний срок" hint="Договорный срок: нарушение показывается, логика не ломается">
            <Input
              type="date"
              value={f.deadline}
              onChange={(e) => set('deadline', e.target.value)}
              className="max-md:h-11"
            />
          </LField>
        </fieldset>

        <fieldset disabled={readOnly} className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <legend className="mb-2 text-[14px] font-semibold">Факт</legend>
          <LField label="Фактическое начало">
            <Input
              type="date"
              value={f.actualStartDay}
              onChange={(e) => set('actualStartDay', e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Время">
            <Input
              type="time"
              value={f.actualStartTime}
              onChange={(e) => set('actualStartTime', e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Фактическое окончание">
            <Input
              type="date"
              value={f.actualFinishDay}
              onChange={(e) => set('actualFinishDay', e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Время">
            <Input
              type="time"
              value={f.actualFinishTime}
              onChange={(e) => set('actualFinishTime', e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Выполнено, %" hint={bySteps ? 'по шагам выполнения ниже' : undefined}>
            <Input
              inputMode="decimal"
              disabled={bySteps}
              value={f.percent}
              onChange={(e) => set('percent', e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          {f.actualStartDay && !f.actualFinishDay ? (
            <LField label="Осталось, раб. дней" hint="Пусто — по проценту">
              <Input
                inputMode="decimal"
                value={f.remainingDays}
                onChange={(e) => set('remainingDays', e.target.value)}
                className="max-md:h-11"
              />
            </LField>
          ) : null}
        </fieldset>

        <fieldset disabled={readOnly} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <legend className="mb-2 text-[14px] font-semibold">Коды работы</legend>
          <LField label="Ответственный">
            <Select
              value={f.responsibleId}
              onChange={(e) => set('responsibleId', e.target.value)}
              className="max-md:h-11"
            >
              <option value="">Не назначен</option>
              {view.people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Подрядчик">
            <Select
              value={f.contractorId}
              onChange={(e) => set('contractorId', e.target.value)}
              className="max-md:h-11"
            >
              <option value="">Своими силами</option>
              {view.contractors.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Захватка">
            <Select value={f.zoneId} onChange={(e) => set('zoneId', e.target.value)} className="max-md:h-11">
              <option value="">Не указана</option>
              {view.zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.code ? `${z.code} ` : ''}
                  {z.name}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Вид работ">
            <Input
              value={f.workType}
              onChange={(e) => set('workType', e.target.value)}
              maxLength={80}
              list="work-types"
              className="max-md:h-11"
            />
            <datalist id="work-types">
              {[...new Set(view.activities.map((x) => x.workType).filter(Boolean))].map((w) => (
                <option key={w!} value={w!} />
              ))}
            </datalist>
          </LField>
          {view.codeTypes.map((t) => (
            <LField key={t.id} label={t.name}>
              <Select
                value={f.codes[t.id] ?? ''}
                onChange={(e) => setF((x) => ({ ...x, codes: { ...x.codes, [t.id]: e.target.value } }))}
                className="max-md:h-11"
              >
                <option value="">Не указано</option>
                {t.values.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.value}
                    {v.title ? ` — ${v.title}` : ''}
                  </option>
                ))}
              </Select>
            </LField>
          ))}
          <LField label="Приоритет при выравнивании" hint="1 — сдвигать последней, 5 — первой">
            <Select
              value={f.priority}
              onChange={(e) => set('priority', e.target.value)}
              disabled={readOnly}
              className="max-md:h-11"
            >
              <option value="">Обычный (3)</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </Select>
          </LField>
          <div className="grid grid-cols-2 gap-3">
            <LField label="Оптимистично, дн" hint="Для вероятностного анализа">
              <Input
                inputMode="decimal"
                value={f.optDays}
                onChange={(e) => set('optDays', e.target.value)}
                disabled={readOnly}
                className="max-md:h-11"
              />
            </LField>
            <LField label="Пессимистично, дн">
              <Input
                inputMode="decimal"
                value={f.pessDays}
                onChange={(e) => set('pessDays', e.target.value)}
                disabled={readOnly}
                className="max-md:h-11"
              />
            </LField>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:col-span-2">
            <LField label="Пикет начала, м" hint="Для графика «время — местоположение»">
              <Input
                inputMode="decimal"
                value={f.locFrom}
                onChange={(e) => set('locFrom', e.target.value)}
                disabled={readOnly}
                className="max-md:h-11"
              />
            </LField>
            <LField label="Пикет конца, м">
              <Input
                inputMode="decimal"
                value={f.locTo}
                onChange={(e) => set('locTo', e.target.value)}
                disabled={readOnly}
                className="max-md:h-11"
              />
            </LField>
          </div>
          <LField label="Примечание" className="sm:col-span-2">
            <Textarea value={f.note} onChange={(e) => set('note', e.target.value)} maxLength={4000} />
          </LField>
        </fieldset>

        {a ? <ActivityScope scheduleId={view.schedule.id} activityId={a.id} canEdit={!readOnly} /> : null}
        {a ? (
          <ActivityResources
            scheduleId={view.schedule.id}
            activityId={a.id}
            canEdit={!readOnly}
            unit={a.unit}
          />
        ) : null}
        {a ? (
          <ActivityPurchases activityId={a.id} projectId={view.schedule.projectId} canEdit={!readOnly} />
        ) : null}
        {a ? (
          <ActivityStepsBlock
            projectId={view.schedule.projectId}
            activityId={a.id}
            workType={a.workType}
            onPercent={(pct, has) => {
              setBySteps(has);
              if (has) set('percent', String(pct));
            }}
          />
        ) : null}
        {a ? (
          <ActivityHolds activityId={a.id} projectId={view.schedule.projectId} canEdit={!readOnly} />
        ) : null}
        {a && !a.actualFinish && a.type !== 'loe' && a.type !== 'hammock' ? (
          <ActivityImpact scheduleId={view.schedule.id} activityId={a.id} />
        ) : null}
        {/* Статьи базы знаний к работе и к её виду работ (БЗ-08); нет статей — панелей нет */}
        {a && staff ? <RelatedArticles type="activity" id={a.id} /> : null}
        {a?.workType && staff ? <RelatedArticles type="work_type" id={a.workType} /> : null}
        <section className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {(['pred', 'succ'] as const).map((side) => {
            const list = rels.filter((r) => (side === 'pred' ? r.succId === id : r.predId === id));
            return (
              <div key={side}>
                <h3 className="mb-1 text-[14px] font-semibold">
                  {side === 'pred' ? 'Предшественники' : 'Последователи'}
                </h3>
                {list.length ? (
                  <ul>{list.map((r) => relRow(r, side))}</ul>
                ) : (
                  <p className="text-[13px] text-ink-2">Нет</p>
                )}
                {!readOnly && !approved ? (
                  <div className="mt-2 flex gap-2">
                    <Select
                      className="min-w-0 flex-1 max-md:h-11"
                      value={side === 'pred' ? addPred : addSucc}
                      onChange={(e) =>
                        side === 'pred' ? setAddPred(e.target.value) : setAddSucc(e.target.value)
                      }
                      aria-label={side === 'pred' ? 'Добавить предшественника' : 'Добавить последователя'}
                    >
                      <option value="">
                        {side === 'pred' ? 'Добавить предшественника…' : 'Добавить последователя…'}
                      </option>
                      {others.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.code} {o.name}
                        </option>
                      ))}
                    </Select>
                    <IconButton
                      label="Добавить связь"
                      className="size-11 md:size-10"
                      onClick={() => {
                        addRel(side, side === 'pred' ? addPred : addSucc);
                        side === 'pred' ? setAddPred('') : setAddSucc('');
                      }}
                    >
                      <Plus className="size-4" />
                    </IconButton>
                  </div>
                ) : null}
                {!readOnly && !approved && side === 'pred' && id ? (
                  <ExternalPicker
                    scheduleId={view.schedule.id}
                    onPick={(x) => {
                      setPicked((m) => new Map(m).set(x.id, x));
                      addRel('pred', x.id);
                    }}
                  />
                ) : null}
              </div>
            );
          })}
        </section>
        {err ? (
          <p className="text-[13px] text-danger" role="alert">
            {err}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function CalendarField({
  view,
  value,
  onChange,
}: {
  view: View;
  value: string;
  onChange: (v: string) => void;
}) {
  const def = view.calendars.find((c) => c.id === view.schedule.calendarId) ?? view.calendars[0];
  return (
    <LField label="Календарь работы">
      <Select value={value} onChange={(e) => onChange(e.target.value)} className="max-md:h-11">
        <option value="">Календарь графика{def ? ` (${def.name})` : ''}</option>
        {view.calendars.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </Select>
    </LField>
  );
}
